import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';
import useToast from '../../hooks/useToast';


const AddStockAdjustment = ({ onBack, onSaveSuccess }) => {
    const { showSuccess, showError, showWarning } = useToast();
    const [locations, setLocations] = useState([]);
    const [form, setForm] = useState({
        location_id: '',
        ref_no: '',
        transaction_date: dateUtils.getChicagoISOString(),
        adjustment_type: 'normal',
        products: [],
        total_amount: '0.00',
        amount_recovered: '0',
        additional_notes: ''
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await apiService.getBusinessLocations();
            setLocations(response.data || []);
            if (response.data?.length > 0) {
                setForm(prev => ({ ...prev, location_id: response.data[0].id }));
            }
        } catch (error) {
      /* console.error('Error fetching locations:', error); */
        }
    };

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            if (searchTerm.length > 1) {
                handleSearch();
            } else {
                setSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(delaySearch);
    }, [searchTerm]);

    const handleSearch = async () => {
        setIsSearching(true);
        try {
            const response = await apiService.getProducts({ search: searchTerm });
            setSearchResults(response.data || response || []);
        } catch (error) {
      /* console.error('Error searching products:', error); */
        } finally {
            setIsSearching(false);
        }
    };

    const addProduct = (product) => {
        const exists = form.products.find(p => p.id === product.id);
        if (exists) {
            showWarning(`"${product.name || product.product}" is already added.`);
            setSearchTerm('');
            setSearchResults([]);
            return;
        }

        const unitPrice = parseFloat(
            product.sell_price_inc_tax || 
            product.default_sell_price || 
            product.purchase_price || 
            product.unit_price || 
            product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
            product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
            product.product_variations?.[0]?.variations?.[0]?.default_purchase_price ||
            0
        );
        // Handle unit as either string or object (with actual_name/short_name properties)
        const unitName = typeof product.unit === 'object' && product.unit !== null
            ? (product.unit.short_name || product.unit.actual_name || 'Pc')
            : (product.unit || 'Pc');
        const variationId = product.product_variations?.[0]?.variations?.[0]?.id
            || product.variation_id
            || null;
        const newProduct = {
            id: product.id,
            variation_id: variationId,
            name: product.name || product.product,
            sku: product.sku,
            unit: unitName,
            quantity: 1,
            unit_price: unitPrice.toFixed(2),
            subtotal: unitPrice.toFixed(2)
        };

        const updatedProducts = [...form.products, newProduct];
        calculateTotal(updatedProducts);
        setForm(prev => ({ ...prev, products: updatedProducts }));
        setSearchTerm('');
        setSearchResults([]);
    };

    const removeProduct = (index) => {
        const updatedProducts = form.products.filter((_, i) => i !== index);
        calculateTotal(updatedProducts);
        setForm(prev => ({ ...prev, products: updatedProducts }));
    };

    const updateProduct = (index, field, value) => {
        const updatedProducts = [...form.products];
        updatedProducts[index][field] = value;
        
        if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(updatedProducts[index].quantity) || 0;
            const price = parseFloat(updatedProducts[index].unit_price) || 0;
            updatedProducts[index].subtotal = (qty * price).toFixed(2);
        }

        calculateTotal(updatedProducts);
        setForm(prev => ({ ...prev, products: updatedProducts }));
    };

    const calculateTotal = (products) => {
        const total = products.reduce((sum, p) => sum + parseFloat(p.subtotal || 0), 0);
        setForm(prev => ({ ...prev, total_amount: total.toFixed(2) }));
    };

    const handleSave = async () => {
        const missing = [];
        if (!form.location_id) missing.push('Business Location');
        if (form.products.length === 0) missing.push('at least one Product');
        if (missing.length > 0) {
            showError(`Please fill in: ${missing.join(', ')}`);
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                location_id:            form.location_id,
                ref_no:                 form.ref_no,
                transaction_date:       form.transaction_date,
                adjustment_type:        form.adjustment_type,
                additional_notes:       form.additional_notes,
                final_total:            form.total_amount,
                total_amount_recovered: form.amount_recovered,
                products: form.products.map(p => ({
                    product_id:   p.id,
                    variation_id: p.variation_id,
                    quantity:     p.quantity,
                    unit_price:   p.unit_price
                }))
            };
            await apiService.createStockAdjustment(payload);
            showSuccess('Stock adjustment saved successfully!');
            if (onSaveSuccess) onSaveSuccess();
            if (onBack) onBack();
        } catch (error) {
      /* console.error('Error saving stock adjustment:', error); */
            showError(error.message || 'Failed to save stock adjustment. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="products-container">
            <div className="main-card">
                <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
                    <button
                      type="button"
                      onClick={onBack}
                      style={{
                        background: 'var(--etpos-card, #fff)',
                        border: '1px solid var(--etpos-border, #e2e8f0)',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        height: '40px',
                        color: 'var(--etpos-text, #1e293b)'
                      }}
                    >
                      ←
                    </button>
                    <h2 style={{ fontSize: '1.25rem', margin: 0 }}><i className="fa fa-plus"></i> Add Stock Adjustment</h2>
                </div>

                <form className="adjustment-form" style={{ padding: '1.5rem' }}>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div className="form-group">
                        <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Business Location:*</label>
                        <select 
                          className="form-input" 
                          value={form.location_id}
                          onChange={(e) => setForm({...form, location_id: e.target.value})}
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                          disabled={isSaving}
                        >
                            {locations.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Reference No:</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={form.ref_no}
                          onChange={(e) => setForm({...form, ref_no: e.target.value})}
                          placeholder="Ref No" 
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                          disabled={isSaving}
                        />
                    </div>
                    <div className="form-group">
                        <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Date:*</label>
                        <input 
                          type="datetime-local" 
                          className="form-input" 
                          value={form.transaction_date}
                          onChange={(e) => setForm({...form, transaction_date: e.target.value})}
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                          disabled={isSaving}
                        />
                    </div>
                    <div className="form-group">
                        <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Adjustment type:*</label>
                        <select 
                          className="form-input" 
                          value={form.adjustment_type}
                          onChange={(e) => setForm({...form, adjustment_type: e.target.value})}
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                          disabled={isSaving}
                        >
                            <option value="normal">Normal</option>
                            <option value="abnormal">Abnormal</option>
                        </select>
                    </div>
                </div>

                <div className="product-search-section" style={{ position: 'relative', marginBottom: '1.5rem' }}>
                    <div style={{ position: 'relative' }}>
                        <input 
                          type="text" 
                          placeholder="Search products for stock adjustment" 
                          className="product-search-input"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onFocus={() => searchTerm.length > 1 && setSearchResults(searchResults)}
                          style={{ width: '100%', padding: '0.7rem 1rem', paddingLeft: '2.5rem', border: '1px solid #3b82f6', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }}
                          disabled={isSaving}
                        />
                        <i className="fa fa-search" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#3b82f6', fontSize: '1rem' }}></i>
                    </div>
                    {isSearching && <div className="product-loading" style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)' }}>Searching products...</div>}
                    {searchResults.length > 0 && (
                        <div className="product-dropdown" style={{ borderRadius: '0.5rem', border: '1px solid #e2e8f0', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', position: 'absolute', width: '100%', zIndex: 100, background: 'var(--etpos-card, #fff)', maxHeight: '300px', overflowY: 'auto' }}>
                            {searchResults.map(p => (
                                <div key={p.id} className="product-dropdown-item" onClick={() => addProduct(p)} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.2s' }}>
                                    <div className="product-name" style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{p.name || p.product} <span className="product-sku-inline" style={{ fontWeight: '400', color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.85rem' }}>({p.sku})</span></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="product-table-container" style={{ marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '0.75rem', background: 'var(--etpos-card, #fff)', overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8125rem', tableLayout: 'fixed' }}>
                        <thead>
                            <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '55%' }}>PRODUCT</th>
                                <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '10%' }}>QUANTITY</th>
                                <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '12%' }}>UNIT PRICE</th>
                                <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '13%' }}>SUBTOTAL</th>
                                <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', textAlign: 'center', width: '10%' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.products.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted, #94a3b8)' }}>No products added. Start searching to add products to the adjustment.</td>
                                </tr>
                            ) : (
                                form.products.map((p, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '0.5rem 0.6rem' }}>
                                            <div style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{p.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)' }}>SKU: {p.sku}</div>
                                        </td>
                                        <td style={{ padding: '0.5rem 0.6rem' }}>
                                            <input 
                                              type="number" 
                                              className="table-input" 
                                              value={p.quantity} 
                                              onChange={(e) => updateProduct(idx, 'quantity', e.target.value)}
                                              style={{ width: '100%', maxWidth: '70px', padding: '0.4rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', fontSize: '0.8125rem' }}
                                              disabled={isSaving}
                                            />
                                        </td>
                                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>
                                            ${parseFloat(p.unit_price).toFixed(2)}
                                        </td>
                                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>${p.subtotal}</td>
                                        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                                            <button 
                                              onClick={() => removeProduct(idx)} 
                                              style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', border: 'none', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}
                                              disabled={isSaving}
                                            >
                                              ✕
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="form-summary-section" style={{ borderTop: '2px solid #f1f5f9', paddingTop: '1.5rem', display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '2rem' }}>
                    <div className="summary-left" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Total amount recovered:</label>
                            <input 
                              type="number" 
                              className="form-input" 
                              value={form.amount_recovered}
                              onChange={(e) => setForm({...form, amount_recovered: e.target.value})}
                              style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' }}
                              disabled={isSaving}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Reason:</label>
                            <textarea 
                              className="form-input" 
                              rows="2"
                              value={form.additional_notes}
                              onChange={(e) => setForm({...form, additional_notes: e.target.value})}
                              placeholder="Reason for adjustment"
                              style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', resize: 'vertical', fontSize: '0.875rem' }}
                              disabled={isSaving}
                            />
                        </div>
                    </div>
                    <div className="summary-right" style={{ background: 'var(--etpos-bg, #f8fafc)', padding: '1.25rem', borderRadius: '1rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                            <span style={{ color: 'var(--etpos-text-muted, #64748b)', fontWeight: '500' }}>Total Items:</span>
                            <span style={{ fontWeight: '700' }}>{form.products.length}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', borderTop: '2px solid #e2e8f0', paddingTop: '1rem' }}>
                            <span style={{ fontWeight: '800', color: 'var(--etpos-text, #1e293b)' }}>Total Amount:</span>
                            <span style={{ fontWeight: '900', color: '#2563eb' }}>${form.total_amount}</span>
                        </div>
                    </div>
                </div>

                <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <button 
                      type="button" 
                      onClick={onBack}
                      style={{ padding: '0.7rem 2rem', background: 'var(--etpos-card, #fff)', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '700', color: 'var(--etpos-text-muted, #475569)', cursor: 'pointer', fontSize: '0.875rem' }}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      onClick={handleSave}
                      disabled={isSaving}
                      style={{ 
                        padding: '0.7rem 2.5rem', 
                        background: isSaving ? 'var(--etpos-text-muted, #94a3b8)' : '#2563eb', 
                        color: 'var(--etpos-card, #fff)', 
                        border: 'none', 
                        borderRadius: '8px', 
                        fontWeight: '700', 
                        cursor: isSaving ? 'not-allowed' : 'pointer', 
                        boxShadow: isSaving ? 'none' : '0 4px 6px -1px rgba(37, 99, 235, 0.3)', 
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {isSaving && <div className="button-spinner"></div>}
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
            </div>
            <style>{`
                .product-dropdown-item:hover {
                    background-color: #f1f5f9;
                }
                .button-spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top: 2px solid #fff;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AddStockAdjustment;
